import styled from 'styled-components';
import { map, compact, join, isEmpty } from 'lodash/fp';
import { connect } from 'react-redux';
import { createStructuredSelector } from 'reselect';
import { array } from 'fp-ts';
import { UnicodeEntity } from '@mews/enums';
import { LocalizedText } from '@mews/framework';
import { TextKey } from '@mews-commander/enums';
import { ProductChargingMode } from '@mews-commander/data-access';
import { ConvertedCurrencyValue, DescriptionItem, SpacedMultiplicationSign } from '@mews-commander/ui';
import { ApplicationState, ProductOrderItem } from '@mews-commander/types';
import { isCurrentEnterpriseInMultiTaxEnvironment } from '@mews-commander/legacy-entities/src/selectors/session/enterprise';
import { getReservationsNonDefaultProductOrderItems } from '@mews-commander/legacy-entities/src/selectors/orderItems';
import { ProductOrderLabel } from '../../../../components/commander-ui';

const TableElement = styled.table`
    width: 100%;
    margin: 0 0 10px;
`;

const ProductOrdersTableRow = ({
    productOrderItem: { id, data, unitAmount, unitMultiplier, personMultiplier, nightMultiplier, productChargingMode },
    isMultiTaxEnvironment,
}: TableRowProps) => {
    const { productId, ageCategoryId } = data.value;
    const chargedPerPerson =
        productChargingMode === ProductChargingMode.PerPerson ||
        productChargingMode === ProductChargingMode.PerPersonPerTimeUnit;
    const chargedPerTimeUnit =
        productChargingMode === ProductChargingMode.PerTimeUnit ||
        productChargingMode === ProductChargingMode.PerPersonPerTimeUnit;

    const counts = compact([
        unitMultiplier > 1 && unitMultiplier,
        chargedPerPerson && personMultiplier,
        chargedPerTimeUnit && nightMultiplier,
    ]);

    const multipliers = isEmpty(counts) ? [unitMultiplier] : counts;

    return (
        <tr key={id}>
            <td>
                <ProductOrderLabel productId={productId} ageCategoryId={ageCategoryId} />
            </td>
            <td>
                {UnicodeEntity.NoBreakSpace}
                {join(SpacedMultiplicationSign, multipliers)}
                {SpacedMultiplicationSign}
                <ConvertedCurrencyValue net={isMultiTaxEnvironment} amount={unitAmount} />
            </td>
        </tr>
    );
};
export const ProductOrders = ({ isMultiTaxEnvironment, productOrders }: ProductOrdersProps) => {
    if (array.isEmpty(productOrders)) {
        return null;
    }

    const definition = (
        <TableElement>
            <tbody>
                {map(
                    (orderItem) => (
                        <ProductOrdersTableRow
                            key={orderItem.id}
                            productOrderItem={orderItem}
                            isMultiTaxEnvironment={isMultiTaxEnvironment}
                        />
                    ),
                    productOrders
                )}
            </tbody>
        </TableElement>
    );

    return (
        <DescriptionItem
            data-test-id="products"
            term={<LocalizedText textKey={TextKey.ProductPlural} />}
            definition={definition}
        />
    );
};
interface TableRowProps {
    isMultiTaxEnvironment: boolean;
    productOrderItem: ProductOrderItem;
}
interface OwnProps {
    serviceOrderId: string;
    serviceId: string;
}
interface StateProps {
    isMultiTaxEnvironment: boolean;
    productOrders: ProductOrderItem[];
}

type ProductOrdersProps = OwnProps & StateProps;

export default connect<StateProps, {}, OwnProps, ApplicationState>(
    createStructuredSelector({
        isMultiTaxEnvironment: (state) => isCurrentEnterpriseInMultiTaxEnvironment(state),
        productOrders: (state: ApplicationState, { serviceId, serviceOrderId }: OwnProps) =>
            getReservationsNonDefaultProductOrderItems(state, { serviceOrderIds: [serviceOrderId], serviceId }),
    })
)(ProductOrders);
